// src/pages/ActualizacionDGR/PlanesPagoDGR.js
//
// Pantalla principal de "Actualización DGR → Planes de Pago".
//
// Diseño: una sola vista con flujo claro
//   1) Pipeline horizontal (Importación → Conciliación → Pasaje)
//   2) Centro de atención (lo que requiere acción)
//   3) Buscador + tabla unificada de planes
//   4) Drawers para acciones especializadas (importar, cambios, lotes, etc.)
//
// Protegida por el permiso ACTUALIZACIÓN DGR (Id_Funcion=110) en módulo CARGA MASIVA.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Box, Paper, Typography, TextField, Button, Chip, Stack,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    TablePagination, Alert, CircularProgress, MenuItem, IconButton, Tooltip,
    FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
    Drawer, Divider, LinearProgress,
} from '@mui/material';
import {
    ReceiptLong as DgrIcon,
    CloudDownload as CloudDownloadIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayIcon,
    FileDownload as FileDownloadIcon,
    ArrowBack as ArrowBackIcon,
    FolderOpen as FolderOpenIcon,
    PendingActions as PendingActionsIcon,
    CompareArrows as CompareArrowsIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Undo as UndoIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Close as CloseIcon,
    ChevronRight as ChevronRightIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    ErrorOutline as ErrorOutlineIcon,
    Inbox as InboxIcon,
    Layers as LayersIcon,
    Visibility as VisibilityIcon,
    Link as LinkIcon,
} from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import usePermisosModulo from '../../hooks/usePermisosModulo';
import DetallePlanDrawer from '../../components/DetallePlanDrawer';
import { parseFechaServer } from '../../utils/fechas';
import {
    dgrPlanesPagoMigrarJWT,
    dgrPlanesPagoListarDetalleJWT,
    dgrPlanesPagoMetricasJWT,
    dgrPasajeListarJWT,
    dgrPasajeLotesJWT,
    dgrPasajeMetricasJWT,
    dgrPasajeEjecutarJWT,
    dgrPasajeActualizarJWT,
    dgrPasajeForzarCrearJWT,
    dgrPasajeVincularAhoraJWT,
    dgrDiferenciasListarJWT,
    dgrDiferenciasContadoresJWT,
    dgrDiferenciaAceptarJWT,
    dgrDiferenciaRechazarJWT,
    dgrDiferenciasAceptarBatchJWT,
    dgrDiferenciasRechazarBatchJWT,
    dgrDiferenciaRevertirJWT,
    dgrDiferenciasExportarJWT,
    dgrDiferenciasPurgarRuidoJWT,
} from '../../api/endpoints';

const COLOR = '#1565c0'; // azul DGR
const BR = 4;

// AppBar global de SIGIF: 64px desktop / 56px mobile. Los drawers se montan
// en Portal raíz; sin top offset su Paper queda detrás del AppBar y el header
// del drawer no se ve.
const APP_BAR_TOP = { xs: '56px', sm: '64px' };
const APP_BAR_HEIGHT_CALC = { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' };

const RESULTADO_LABELS = {
    V: 'Conciliado',
    X: 'Sin coincidencia',
    S: 'Sin fondos',
    D: 'Duplicado',
    W: 'Sin solicitud',
};

const RESULTADO_COLORS = {
    V: 'success',
    X: 'error',
    S: 'warning',
    D: 'default',
    W: 'info',
};

function fmtMonto(n) {
    return Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });
}

// Las columnas DATE de SQL Server llegan como ISO 'YYYY-MM-DDT00:00:00.000Z'.
// Usar new Date(...).toLocaleDateString() las corre -1 día (medianoche UTC →
// día anterior en UTC-3). Extraemos los componentes literales del string.
function fmtFecha(d) {
    if (!d) return '';
    const s = String(d);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const x = parseFechaServer(d);
    return x ? x.toLocaleDateString('es-AR') : '';
}

export default function PlanesPagoDGR() {
    const { enqueueSnackbar } = useSnackbar();
    const permisos = usePermisosModulo('CARGA MASIVA');
    const puedeVer = permisos.has('ACTUALIZACIÓN DGR', 'SELECT');
    const puedeMigrar = permisos.has('ACTUALIZACIÓN DGR', 'UPDATE');

    const [metricas, setMetricas] = useState(null);
    const [metricasPasaje, setMetricasPasaje] = useState(null);
    const [contadoresDif, setContadoresDif] = useState({ pendientes: 0, aceptadas: 0, rechazadas: 0, revertidas: 0 });
    const [esSimulado, setEsSimulado] = useState(false);

    // Drawers
    const [drawer, setDrawer] = useState(null); // 'importar' | 'cambios' | 'lotes' | 'pendExp' | { tipo: 'plan', id }
    // Filtro pre-aplicado a la tabla central (cuando se click una pipeline card o item de atención)
    const [filtroInicial, setFiltroInicial] = useState(null);
    // Triggers SEPARADOS: la tabla y las métricas se recargan independientemente.
    // Las métricas pegan a /metricas (queries pesadas que tardaban 25-30s y
    // disparaban ETIMEOUT al spamear). Solo deben recargarse:
    //   - al montar
    //   - al apretar el botón "Refrescar" global
    //   - cuando una operación realmente cambió los datos (importar, aceptar/
    //     rechazar/revertir diferencia, pasar a expediente, forzar crear).
    // Los clicks en pipeline cards y centro de atención SOLO filtran la tabla.
    const [reloadTabla, setReloadTabla] = useState(0);
    const [reloadMetricas, setReloadMetricas] = useState(0);
    const [cargandoMetricas, setCargandoMetricas] = useState(false);

    const cargarMetricas = useCallback(async () => {
        setCargandoMetricas(true);
        // Cada métrica se maneja por separado para que el fallo de una no
        // tire al resto (antes con Promise.all si una daba 500 perdías todo).
        try {
            const r = await dgrPlanesPagoMetricasJWT();
            if (r?.ok) { setMetricas(r.metricas); setEsSimulado(!!r.simulado); }
        } catch { /* silencioso, ver chip */ }
        try {
            const r = await dgrPasajeMetricasJWT();
            if (r?.ok) setMetricasPasaje(r.metricas);
        } catch { /* silencioso */ }
        try {
            const r = await dgrDiferenciasContadoresJWT();
            if (r?.ok) setContadoresDif(r.contadores);
        } catch { /* silencioso */ }
        setCargandoMetricas(false);
    }, []);

    useEffect(() => { if (puedeVer) cargarMetricas(); }, [puedeVer, cargarMetricas, reloadMetricas]);

    // "Refrescar todo" — botón explícito + post-operación
    const refrescarTodo = () => { setReloadMetricas(k => k + 1); setReloadTabla(k => k + 1); };

    // "Vincular ahora": fuerza la vinculación masiva de planes Incompletos a
    // expedientes existentes. El cron diario lo hace solo; este botón es para
    // forzarlo desde la UI cuando recién se cargaron expedientes nuevos.
    const [vinculandoAhora, setVinculandoAhora] = useState(false);
    const vincularAhora = async () => {
        setVinculandoAhora(true);
        try {
            const r = await dgrPasajeVincularAhoraJWT();
            if (r?.ok) {
                enqueueSnackbar(r.mensaje || `Vinculados ${r.vinculados} de ${r.procesados}`, { variant: 'success' });
                refrescarTodo();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al vincular', { variant: 'error' });
            }
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
        finally { setVinculandoAhora(false); }
    };
    // "Solo tabla" — pipeline cards / filtros / centro de atención
    const refrescarTabla = () => { setReloadTabla(k => k + 1); };

    if (!permisos.ready) {
        return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    }
    if (!puedeVer) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">
                    No tenés permiso para ver esta pantalla. Pedile a un director que te habilite la función
                    <strong> ACTUALIZACIÓN DGR</strong> en el módulo <strong>CARGA MASIVA</strong>.
                </Alert>
            </Box>
        );
    }

    const m = metricas || {};
    const mp = metricasPasaje || {};
    const aRevisar = (m.SinCoincidencia || 0) + (m.SinFondos || 0) + (m.SinSolicitud || 0) + (m.Duplicados || 0);
    const items = [];
    if (contadoresDif.pendientes > 0) {
        items.push({
            key: 'cambios', icon: <CompareArrowsIcon sx={{ color: '#f57c00' }} />, color: '#f57c00',
            label: `${contadoresDif.pendientes} cambios detectados`,
            sub: 'Diferencias entre WS-DGR y datos locales, esperan tu revisión',
            onClick: () => setDrawer('cambios'),
        });
    }
    if (aRevisar > 0) {
        items.push({
            key: 'revisar', icon: <ErrorOutlineIcon sx={{ color: '#c62828' }} />, color: '#c62828',
            label: `${aRevisar.toLocaleString('es-AR')} planes con problema de conciliación`,
            sub: `${m.SinCoincidencia || 0} sin coincidencia · ${m.SinFondos || 0} sin pagos · ${m.SinSolicitud || 0} sin solicitud · ${m.Duplicados || 0} duplicados`,
            onClick: () => { setFiltroInicial({ resultado: 'X' }); refrescarTabla(); },
        });
    }
    if ((mp.Pendientes || 0) > 0) {
        items.push({
            key: 'pendExp', icon: <PendingActionsIcon sx={{ color: '#1565c0' }} />, color: '#1565c0',
            label: `${(mp.Pendientes || 0).toLocaleString('es-AR')} planes conciliados sin expediente`,
            sub: 'Carga Masiva los vincula automáticamente, o podés forzar la creación',
            onClick: () => setDrawer('pendExp'),
        });
    }

    return (
        <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 2, px: { xs: 2, md: 3 } }}>
            <Container maxWidth={false} disableGutters>
                {/* ── Header ────────────────────────────────────────────────── */}
                <Stack direction="row" alignItems="center" sx={{ mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
                    <DgrIcon sx={{ color: COLOR, fontSize: 36 }} />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR }}>
                            Actualización DGR — Planes de Pago
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Importar planes del WS de Rentas, conciliar y pasar a expediente
                        </Typography>
                    </Box>
                    {esSimulado && <Chip label="MODO SIMULACIÓN" color="warning" size="small" sx={{ fontWeight: 700 }} />}
                    <Tooltip title="Refrescar todo">
                        <IconButton onClick={refrescarTodo} disabled={cargandoMetricas}>
                            {cargandoMetricas ? <CircularProgress size={20} /> : <RefreshIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Busca expedientes recién creados y vincula los planes 'Incompletos' que ahora pueden cruzarse. El cron diario lo hace automáticamente cada noche.">
                        <span>
                            <Button variant="outlined" startIcon={vinculandoAhora ? <CircularProgress size={16} /> : <LinkIcon />}
                                onClick={vincularAhora} disabled={!puedeMigrar || vinculandoAhora}
                                sx={{ color: COLOR, borderColor: COLOR, textTransform: 'none', fontWeight: 600 }}>
                                Vincular ahora
                            </Button>
                        </span>
                    </Tooltip>
                    <Button variant="contained" startIcon={<CloudDownloadIcon />}
                        onClick={() => setDrawer('importar')} disabled={!puedeMigrar}
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#0d47a1' }, textTransform: 'none', fontWeight: 600 }}>
                        Importar del WS-DGR
                    </Button>
                </Stack>

                {/* ── Pipeline ────────────────────────────────────────────────── */}
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 32px 1fr 32px 1fr' }, alignItems: 'stretch', mb: 2 }}>
                    <PipelineCard
                        num="1" icon={<InboxIcon />} titulo="IMPORTACIÓN" color="#1565c0"
                        valorPrincipal={(m.TotalRegistros || 0).toLocaleString('es-AR')}
                        valorLabel="registros importados"
                        lineas={[
                            { label: 'Lotes diarios', valor: (m.TotalLotes || 0).toLocaleString('es-AR') },
                            { label: 'Última actualización',
                              valor: m.UltimaActualizacion_Fecha
                                ? `${fmtFecha(m.UltimaActualizacion_Fecha)}${m.UltimaActualizacion_Hora ? ' ' + String(m.UltimaActualizacion_Hora).slice(0,5) : ''}`
                                : '—' },
                            { label: 'Por', valor: m.UltimaActualizacion_Usuario || '—' },
                            { label: 'Cron diario', valor: 'Activo (6:00 AM)' },
                        ]}
                        onClick={() => { setFiltroInicial(null); refrescarTabla(); }}
                    />
                    <FlechaPipeline />
                    <PipelineCard
                        num="2" icon={<AssignmentTurnedInIcon />} titulo="CONCILIACIÓN" color={aRevisar > 0 ? '#f57c00' : '#2e7d32'}
                        valorPrincipal={(m.Conciliados || 0).toLocaleString('es-AR')}
                        valorLabel="conciliados"
                        lineas={[
                            { label: 'Sin coincidencia', valor: (m.SinCoincidencia || 0).toLocaleString('es-AR'), warn: (m.SinCoincidencia || 0) > 0 },
                            { label: 'Sin pagos', valor: (m.SinFondos || 0).toLocaleString('es-AR'), warn: (m.SinFondos || 0) > 0 },
                            { label: 'Sin solicitud', valor: (m.SinSolicitud || 0).toLocaleString('es-AR'), warn: (m.SinSolicitud || 0) > 0 },
                            { label: 'Duplicados', valor: (m.Duplicados || 0).toLocaleString('es-AR') },
                        ]}
                        onClick={() => { setFiltroInicial({ resultado: 'V' }); refrescarTabla(); }}
                    />
                    <FlechaPipeline />
                    <PipelineCard
                        num="3" icon={<FolderOpenIcon />} titulo="PASAJE A EXPEDIENTE" color={(mp.Pendientes || 0) > 0 ? '#1565c0' : '#2e7d32'}
                        valorPrincipal={(mp.Pasados || 0).toLocaleString('es-AR')}
                        valorLabel="ya pasados a expediente"
                        lineas={[
                            { label: 'Pendientes', valor: (mp.Pendientes || 0).toLocaleString('es-AR'), warn: (mp.Pendientes || 0) > 0 },
                        ]}
                        accion={(mp.Pendientes || 0) > 0 && puedeMigrar ? { label: 'Ver lotes', onClick: () => setDrawer('lotes') } : null}
                        onClick={() => setDrawer('lotes')}
                    />
                </Box>

                {/* ── Centro de atención ────────────────────────────────────── */}
                {items.length > 0 && (
                    <Paper sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px`, borderLeft: '4px solid #f57c00' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#666' }}>
                            ⚠ Requieren tu atención ({items.length})
                        </Typography>
                        <Stack divider={<Divider />} spacing={0}>
                            {items.map(it => (
                                <AtencionItem key={it.key} icon={it.icon} color={it.color}
                                    label={it.label} sub={it.sub} onClick={it.onClick} />
                            ))}
                        </Stack>
                    </Paper>
                )}

                {/* ── Tabla unificada ───────────────────────────────────────── */}
                <Paper sx={{ borderRadius: `${BR}px`, p: 0 }}>
                    <TablaPlanesUnificada
                        filtroInicial={filtroInicial}
                        puedeMigrar={puedeMigrar}
                        enqueueSnackbar={enqueueSnackbar}
                        onVerPlan={(id) => setDrawer({ tipo: 'plan', id })}
                        onAbrirLotes={() => setDrawer('lotes')}
                        reloadKey={reloadTabla}
                    />
                </Paper>
            </Container>

            {/* ── Drawers ──────────────────────────────────────────────────── */}
            <Drawer anchor="right" open={drawer === 'importar'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: { width: { xs: '100%', md: 540 }, top: APP_BAR_TOP, height: APP_BAR_HEIGHT_CALC } }}>
                {drawer === 'importar' && (
                    <DrawerImportar
                        puedeMigrar={puedeMigrar} enqueueSnackbar={enqueueSnackbar}
                        onClose={() => setDrawer(null)} onResultado={refrescarTodo}
                        onIrACambios={() => setDrawer('cambios')} esSimulado={esSimulado}
                    />
                )}
            </Drawer>

            <Drawer anchor="right" open={drawer === 'cambios'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: { width: { xs: '100%', md: '85%', lg: '70%' }, top: APP_BAR_TOP, height: APP_BAR_HEIGHT_CALC } }}>
                {drawer === 'cambios' && (
                    <DrawerCambios puedeMigrar={puedeMigrar} enqueueSnackbar={enqueueSnackbar}
                        onClose={() => setDrawer(null)} onCambio={refrescarTodo} />
                )}
            </Drawer>

            <Drawer anchor="right" open={drawer === 'lotes'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: { width: { xs: '100%', md: '85%', lg: '75%' }, top: APP_BAR_TOP, height: APP_BAR_HEIGHT_CALC } }}>
                {drawer === 'lotes' && (
                    <DrawerLotes puedeMigrar={puedeMigrar} enqueueSnackbar={enqueueSnackbar}
                        onClose={() => setDrawer(null)} onCambio={refrescarTodo} />
                )}
            </Drawer>

            <Drawer anchor="right" open={drawer === 'pendExp'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: { width: { xs: '100%', md: '70%', lg: '60%' }, top: APP_BAR_TOP, height: APP_BAR_HEIGHT_CALC } }}>
                {drawer === 'pendExp' && (
                    <DrawerPendientesExp puedeMigrar={puedeMigrar} enqueueSnackbar={enqueueSnackbar}
                        onClose={() => setDrawer(null)} onCambio={refrescarTodo} />
                )}
            </Drawer>

            {/* Super-drawer único de detalle del Plan — reusable en todos los módulos */}
            <DetallePlanDrawer
                open={drawer?.tipo === 'plan'}
                idDetalle={drawer?.id}
                onClose={() => setDrawer(null)}
            />
        </Box>
    );
}

// ============================================================================
// Componentes visuales del rediseño
// ============================================================================

function PipelineCard({ num, icon, titulo, color, valorPrincipal, valorLabel, lineas = [], accion, onClick }) {
    return (
        <Paper variant="outlined" onClick={onClick}
            sx={{
                p: 2, borderRadius: `${BR}px`, cursor: onClick ? 'pointer' : 'default',
                borderTop: `3px solid ${color}`, transition: 'all 0.15s',
                '&:hover': onClick ? { boxShadow: 2, transform: 'translateY(-2px)' } : {},
                display: 'flex', flexDirection: 'column', gap: 1,
            }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ bgcolor: color, color: 'white', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                    {num}
                </Box>
                <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', letterSpacing: 0.5 }}>
                    {titulo}
                </Typography>
            </Stack>
            <Box sx={{ pl: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
                    {valorPrincipal}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {valorLabel}
                </Typography>
            </Box>
            {lineas.length > 0 && (
                <Stack spacing={0.25} sx={{ pt: 0.5, borderTop: '1px dashed #eee' }}>
                    {lineas.map((l, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: l.warn ? '#f57c00' : 'inherit' }}>
                                {l.valor}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            )}
            {accion && (
                <Button size="small" variant="outlined"
                    onClick={(e) => { e.stopPropagation(); accion.onClick(); }}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', mt: 0.5, color, borderColor: color }}>
                    {accion.label}
                </Button>
            )}
        </Paper>
    );
}

function FlechaPipeline() {
    return (
        <Box sx={{
            display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center',
            color: '#bbb',
        }}>
            <ChevronRightIcon sx={{ fontSize: 32 }} />
        </Box>
    );
}

function AtencionItem({ icon, color, label, sub, onClick }) {
    return (
        <Box onClick={onClick} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 0.5,
            cursor: 'pointer', borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
        }}>
            <Box sx={{ display: 'flex' }}>{icon}</Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color }}>
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {sub}
                </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: '#999' }} />
        </Box>
    );
}

// ============================================================================
// DrawerImportar — antes TabMigrar
// ============================================================================
function DrawerImportar({ puedeMigrar, enqueueSnackbar, onClose, onResultado, onIrACambios, esSimulado }) {
    const hoy = new Date().toISOString().slice(0, 10);
    const hace7 = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const fechaMinima = new Date(Date.now() - 2 * 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    const [fechaDesde, setFechaDesde] = useState(hace7);
    const [fechaHasta, setFechaHasta] = useState(hoy);
    const [migrando, setMigrando] = useState(false);
    const [resultado, setResultado] = useState(null);

    const handleMigrar = async () => {
        if (!fechaDesde || !fechaHasta) {
            enqueueSnackbar('Completá el rango de fechas', { variant: 'warning' });
            return;
        }
        setMigrando(true);
        setResultado(null);
        try {
            const r = await dgrPlanesPagoMigrarJWT(fechaDesde, fechaHasta);
            if (r?.ok) {
                setResultado(r);
                enqueueSnackbar(r.mensaje, { variant: 'success' });
                onResultado?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al migrar', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar('Error al consultar DGR', { variant: 'error' });
        } finally { setMigrando(false); }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fafbfc' }}>
                <CloudDownloadIcon sx={{ color: COLOR }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR, lineHeight: 1.2 }}>
                        Importar planes desde WS-DGR
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Consulta día por día y upserta los detalles
                    </Typography>
                </Box>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                {esSimulado && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Modo simulación.</strong> El WS real no está activo. Los datos generados son ficticios.
                    </Alert>
                )}

                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    Rango permitido: desde {fmtFecha(fechaMinima)} hasta hoy ({fmtFecha(hoy)}).
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                    <TextField size="small" label="Fecha desde" type="date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: fechaMinima, max: hoy }}
                        value={fechaDesde}
                        onChange={e => setFechaDesde(e.target.value)}
                        sx={{ minWidth: 180 }} />
                    <TextField size="small" label="Fecha hasta" type="date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: fechaMinima, max: hoy }}
                        value={fechaHasta}
                        onChange={e => setFechaHasta(e.target.value)}
                        sx={{ minWidth: 180 }} />
                </Stack>
                <Button fullWidth variant="contained" size="large"
                    startIcon={migrando ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CloudDownloadIcon />}
                    onClick={handleMigrar}
                    disabled={migrando || !puedeMigrar}
                    sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#0d47a1' }, textTransform: 'none', fontWeight: 700 }}>
                    {migrando ? 'Consultando WS-DGR...' : 'Migrar planes del rango'}
                </Button>
                {!puedeMigrar && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Tenés acceso de solo lectura.
                    </Alert>
                )}

            {resultado && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: `${BR}px` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Resultado</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`${resultado.diasProcesados} días procesados`} color="primary" size="small" />
                        <Chip label={`${resultado.totalInsertados} insertados`} color="success" size="small" />
                        <Chip label={`${resultado.totalSinCambios || 0} sin cambios`} color="default" size="small" variant="outlined" />
                        {(resultado.totalDiferencias || 0) > 0 && (
                            <Chip
                                label={`${resultado.totalDiferencias} con diferencias — revisar`}
                                color="warning" size="small"
                                onClick={onIrACambios}
                                sx={{ cursor: 'pointer', fontWeight: 600 }}
                                icon={<CompareArrowsIcon style={{ fontSize: 16 }} />} />
                        )}
                        {resultado.simulado && <Chip label="SIMULACIÓN" color="warning" size="small" />}
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#e3f2fd', fontWeight: 700, color: COLOR } }}>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell align="center">Estado</TableCell>
                                    <TableCell align="center">Lote</TableCell>
                                    <TableCell align="right">Insertados</TableCell>
                                    <TableCell align="right">Sin cambios</TableCell>
                                    <TableCell align="right">Diferencias</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {resultado.lotes.map(l => {
                                    const sinDatos = l.idLote === 0;
                                    const tieneDif = (l.diferencias || 0) > 0;
                                    return (
                                        <TableRow key={l.fecha} hover sx={tieneDif ? { bgcolor: '#fff8e1' } : {}}>
                                            <TableCell>{fmtFecha(l.fecha)}</TableCell>
                                            <TableCell align="center">
                                                {sinDatos
                                                    ? <Chip size="small" label="sin datos en el WS" color="default" variant="outlined" />
                                                    : l.creado
                                                        ? <Chip size="small" label="lote nuevo" color="success" />
                                                        : <Chip size="small" label="re-consulta" color="info" variant="outlined" />}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontFamily: 'monospace', color: sinDatos ? 'text.disabled' : 'inherit' }}>
                                                {sinDatos ? '—' : l.idLote}
                                            </TableCell>
                                            <TableCell align="right">{l.insertados}</TableCell>
                                            <TableCell align="right">{l.sinCambios || 0}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: tieneDif ? 700 : 400, color: tieneDif ? '#f57c00' : 'inherit' }}>
                                                {l.diferencias || 0}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
            </Box>
        </Box>
    );
}

// ============================================================================
// TablaPlanesUnificada — la tabla central de la nueva pantalla
// ============================================================================
function TablaPlanesUnificada({ filtroInicial, puedeMigrar, enqueueSnackbar, onVerPlan, onAbrirLotes, reloadKey }) {
    const [filtros, setFiltros] = useState({
        fechaDesde: '', fechaHasta: '',
        resultado: filtroInicial?.resultado || '',
        refNroPlan: '', numero: '',
    });
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [total, setTotal] = useState(0);
    const [seleccionados, setSeleccionados] = useState(new Set());
    const [exportando, setExportando] = useState(false);

    // Cuando viene un filtro inicial nuevo (click en pipeline o atención),
    // lo aplicamos y volvemos a la página 0.
    useEffect(() => {
        if (filtroInicial?.resultado !== undefined) {
            setFiltros(f => ({ ...f, resultado: filtroInicial?.resultado || '' }));
            setPage(0);
        }
    }, [filtroInicial]);

    const cargar = useCallback(async (pagina = page, ps = pageSize) => {
        setLoading(true);
        try {
            const r = await dgrPlanesPagoListarDetalleJWT({
                fechaDesde: filtros.fechaDesde || undefined,
                fechaHasta: filtros.fechaHasta || undefined,
                resultado: filtros.resultado || undefined,
                refNroPlan: filtros.refNroPlan || undefined,
                numero: filtros.numero || undefined,
                page: pagina + 1, pageSize: ps,
            });
            if (r?.ok) {
                setRegistros(r.registros || []);
                setTotal(r.total || 0);
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al consultar', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al consultar', { variant: 'error' });
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros, page, pageSize]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { cargar(0, pageSize); }, [filtros.resultado, reloadKey]);

    const handleBuscar = () => { setPage(0); cargar(0, pageSize); };
    const handleLimpiar = () => {
        setFiltros({ fechaDesde: '', fechaHasta: '', resultado: '', refNroPlan: '', numero: '' });
        setPage(0);
    };

    const titulo = filtros.resultado
        ? `Planes con estado "${RESULTADO_LABELS[filtros.resultado] || filtros.resultado}"`
        : 'Todos los planes';

    // ─── Selección ──────────────────────────────────────────────────────────
    const toggleSel = (id) => {
        const s = new Set(seleccionados);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSeleccionados(s);
    };
    const toggleAllPagina = () => {
        if (registros.every(r => seleccionados.has(r.Id))) {
            const s = new Set(seleccionados);
            registros.forEach(r => s.delete(r.Id));
            setSeleccionados(s);
        } else {
            const s = new Set(seleccionados);
            registros.forEach(r => s.add(r.Id));
            setSeleccionados(s);
        }
    };
    const seleccionarTodos = async () => {
        // Trae todos los registros con los filtros actuales y los selecciona.
        // Cap defensivo de 10.000 (igual que otros exports del sistema).
        setExportando(true);
        try {
            const r = await dgrPlanesPagoListarDetalleJWT({
                fechaDesde: filtros.fechaDesde || undefined,
                fechaHasta: filtros.fechaHasta || undefined,
                resultado: filtros.resultado || undefined,
                refNroPlan: filtros.refNroPlan || undefined,
                numero: filtros.numero || undefined,
                page: 1, pageSize: 10000,
            });
            if (r?.ok) {
                const s = new Set((r.registros || []).map(x => x.Id));
                setSeleccionados(s);
                enqueueSnackbar(`${s.size} planes seleccionados`, { variant: 'info' });
            }
        } catch { enqueueSnackbar('Error al seleccionar todos', { variant: 'error' }); }
        finally { setExportando(false); }
    };
    const limpiarSel = () => setSeleccionados(new Set());

    // ─── Export ────────────────────────────────────────────────────────────
    // Si hay selección, exporta los seleccionados; sino, exporta todos los
    // que matchean el filtro actual (cap 10.000).
    const cargarParaExport = async () => {
        if (seleccionados.size > 0) {
            // Re-leemos por ID para garantizar datos frescos en bulk
            const r = await dgrPlanesPagoListarDetalleJWT({
                fechaDesde: filtros.fechaDesde || undefined,
                fechaHasta: filtros.fechaHasta || undefined,
                resultado: filtros.resultado || undefined,
                refNroPlan: filtros.refNroPlan || undefined,
                numero: filtros.numero || undefined,
                page: 1, pageSize: 10000,
            });
            if (r?.ok) return (r.registros || []).filter(x => seleccionados.has(x.Id));
            return [];
        }
        const r = await dgrPlanesPagoListarDetalleJWT({
            fechaDesde: filtros.fechaDesde || undefined,
            fechaHasta: filtros.fechaHasta || undefined,
            resultado: filtros.resultado || undefined,
            refNroPlan: filtros.refNroPlan || undefined,
            numero: filtros.numero || undefined,
            page: 1, pageSize: 10000,
        });
        return r?.ok ? (r.registros || []) : [];
    };

    const tituloReporte = () => {
        const partes = ['Planes de Pago DGR'];
        if (filtros.resultado) partes.push(`Estado: ${RESULTADO_LABELS[filtros.resultado] || filtros.resultado}`);
        if (filtros.fechaDesde) partes.push(`Desde: ${fmtFecha(filtros.fechaDesde)}`);
        if (filtros.fechaHasta) partes.push(`Hasta: ${fmtFecha(filtros.fechaHasta)}`);
        if (filtros.refNroPlan) partes.push(`Plan: ${filtros.refNroPlan}`);
        if (filtros.numero) partes.push(`Certificado: ${filtros.numero}`);
        if (seleccionados.size > 0) partes.push(`Selección manual (${seleccionados.size})`);
        return partes.join(' — ');
    };

    const filaPlana = (r) => ({
        fecha: fmtFecha(r.fpago),
        plan: r.ref_nro_plan,
        cert: r.letra ? `${r.letra}-${r.numero}` : (r.numero || ''),
        deuda: Number(r.deuda_certificado || 0),
        costas: Number(r.costas || 0),
        costas_iva: Number(r.costas_iva || 0),
        gastos: Number(r.gastos || 0),
        cuota: r.Num_Cuota || '',
        estado: r.Estado_Conciliacion ? `${r.Estado_Conciliacion} - ${RESULTADO_LABELS[r.Estado_Conciliacion] || ''}` : (r.Resultado_Cadena || ''),
        demandado: r.Demandado_NN || '',
        direccion: r.Demandado_Dom || '',
        expediente: r.Expediente_Autos || '',
        exp_id: r.Expediente_Id || '',
    });

    const handleExportExcel = async () => {
        setExportando(true);
        try {
            const items = await cargarParaExport();
            const filas = items.map(filaPlana);
            const wb = XLSX.utils.book_new();
            const head = [[
                'Fecha Pago', 'Nº Plan', 'Certificado', 'Deuda', 'Costas', 'Costas IVA', 'Gastos',
                'Cuota', 'Estado', 'Demandado', 'Dirección', 'Expediente', 'Exp Id',
            ]];
            const body = filas.map(f => [
                f.fecha, f.plan, f.cert, f.deuda, f.costas, f.costas_iva, f.gastos,
                f.cuota, f.estado, f.demandado, f.direccion, f.expediente, f.exp_id,
            ]);
            const ws = XLSX.utils.aoa_to_sheet([...head, ...body]);
            ws['!cols'] = [
                { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
                { wch: 8 }, { wch: 26 }, { wch: 36 }, { wch: 30 }, { wch: 18 }, { wch: 10 },
            ];
            XLSX.utils.book_append_sheet(wb, ws, 'Planes DGR');
            const fecha = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Planes_DGR_${fecha}.xlsx`);
            enqueueSnackbar(`${filas.length} planes exportados a Excel`, { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Error al exportar', { variant: 'error' });
        } finally { setExportando(false); }
    };

    const handleExportPDF = async () => {
        setExportando(true);
        try {
            const items = await cargarParaExport();
            const filas = items.map(filaPlana);
            const doc = new jsPDF({ orientation: 'landscape' });
            const margin = 14;
            doc.setFontSize(14);
            doc.setTextColor(21, 101, 192);
            doc.text(tituloReporte(), margin, 15);
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(`${filas.length} registro(s) · Generado: ${new Date().toLocaleString('es-AR')}`, margin, 21);

            autoTable(doc, {
                startY: 26,
                head: [['Fecha', 'Plan', 'Cert.', 'Deuda', 'Cuota', 'Estado', 'Demandado', 'Expediente']],
                body: filas.map(f => [
                    f.fecha, f.plan, f.cert,
                    f.deuda.toLocaleString('es-AR', { minimumFractionDigits: 2 }),
                    f.cuota, f.estado, f.demandado, f.expediente || '-',
                ]),
                theme: 'striped',
                headStyles: { fillColor: [21, 101, 192], textColor: 255 },
                styles: { fontSize: 8, cellPadding: 1.5 },
                columnStyles: {
                    0: { cellWidth: 18 }, 1: { cellWidth: 22 }, 2: { cellWidth: 22 },
                    3: { cellWidth: 24, halign: 'right' }, 4: { cellWidth: 12, halign: 'center' },
                    5: { cellWidth: 35 }, 6: { cellWidth: 68 }, 7: { cellWidth: 28 },
                },
                margin: { left: margin, right: margin },
            });

            const fecha = new Date().toISOString().slice(0, 10);
            const filename = `Planes_DGR_${fecha}.pdf`;
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) doc.save(filename);
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            enqueueSnackbar(`${filas.length} planes exportados a PDF`, { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Error al exportar PDF', { variant: 'error' });
        } finally { setExportando(false); }
    };

    const todosPaginaSeleccionados = registros.length > 0 && registros.every(r => seleccionados.has(r.Id));
    const algunoPaginaSeleccionado = registros.some(r => seleccionados.has(r.Id));

    return (
        <Box>
            {/* Barra superior */}
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', borderBottom: '1px solid #eee' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLOR }}>
                    {titulo}
                </Typography>
                <Chip size="small" label={`${total.toLocaleString('es-AR')} registros`} color="default" />
                {seleccionados.size > 0 && (
                    <Chip size="small" label={`${seleccionados.size} seleccionado(s)`} color="primary"
                        onDelete={limpiarSel} />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Seleccionar TODOS los planes (con filtros actuales)">
                    <span>
                        <Button size="small" variant="outlined" onClick={seleccionarTodos} disabled={exportando || total === 0}
                            sx={{ textTransform: 'none' }}>
                            Sel. todos ({total.toLocaleString('es-AR')})
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={`Exportar ${seleccionados.size > 0 ? seleccionados.size + ' seleccionados' : 'todos los filtrados'} a Excel`}>
                    <span>
                        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                            onClick={handleExportExcel} disabled={exportando || total === 0}
                            sx={{ textTransform: 'none' }}>
                            Excel
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={`Exportar ${seleccionados.size > 0 ? seleccionados.size + ' seleccionados' : 'todos los filtrados'} a PDF (preview en nueva pestaña)`}>
                    <span>
                        <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPDF} disabled={exportando || total === 0}
                            sx={{ textTransform: 'none' }}>
                            PDF
                        </Button>
                    </span>
                </Tooltip>
                <Button size="small" variant="outlined" startIcon={<LayersIcon />} onClick={onAbrirLotes}
                    sx={{ textTransform: 'none', color: COLOR, borderColor: COLOR }}>
                    Ver por lotes
                </Button>
            </Box>

            {/* Filtros */}
            <Box sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', bgcolor: '#fafbfc' }}>
                <TextField size="small" label="Fecha pago desde" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filtros.fechaDesde}
                    onChange={e => setFiltros(f => ({ ...f, fechaDesde: e.target.value }))}
                    sx={{ minWidth: 150 }} />
                <TextField size="small" label="Fecha pago hasta" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filtros.fechaHasta}
                    onChange={e => setFiltros(f => ({ ...f, fechaHasta: e.target.value }))}
                    sx={{ minWidth: 150 }} />
                <TextField size="small" select label="Estado"
                    value={filtros.resultado}
                    onChange={e => setFiltros(f => ({ ...f, resultado: e.target.value }))}
                    sx={{ minWidth: 180 }}>
                    <MenuItem value="">Todos los estados</MenuItem>
                    <MenuItem value="V">Conciliado</MenuItem>
                    <MenuItem value="X">Sin coincidencia</MenuItem>
                    <MenuItem value="S">Sin pagos</MenuItem>
                    <MenuItem value="W">Sin solicitud</MenuItem>
                    <MenuItem value="D">Duplicado</MenuItem>
                </TextField>
                <TextField size="small" label="Nº Plan" type="number"
                    value={filtros.refNroPlan}
                    onChange={e => setFiltros(f => ({ ...f, refNroPlan: e.target.value }))}
                    sx={{ minWidth: 120 }} />
                <TextField size="small" label="Nº Certificado" type="number"
                    value={filtros.numero}
                    onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))}
                    sx={{ minWidth: 140 }} />
                <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={handleBuscar}
                    sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#0d47a1' }, textTransform: 'none' }}>
                    Buscar
                </Button>
                <Button size="small" variant="text" onClick={handleLimpiar} sx={{ textTransform: 'none' }}>
                    Limpiar
                </Button>
                <Tooltip title="Refrescar">
                    <IconButton onClick={() => cargar(page, pageSize)} size="small"><RefreshIcon fontSize="small" /></IconButton>
                </Tooltip>
            </Box>

            {loading && <LinearProgress />}

            {!loading && registros.length === 0 ? (
                <Alert severity="info" sx={{ m: 2 }}>
                    No hay registros con los filtros aplicados.
                </Alert>
            ) : (
                <>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 480px)', minHeight: 300 }}>
                        <Table size="small" stickyHeader sx={{ '& td, & th': { fontSize: '0.76rem', py: 0.5, px: 1 } }}>
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#e3f2fd', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap' } }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox size="small"
                                            checked={todosPaginaSeleccionados}
                                            indeterminate={algunoPaginaSeleccionado && !todosPaginaSeleccionados}
                                            onChange={toggleAllPagina} />
                                    </TableCell>
                                    <TableCell>Fecha Pago</TableCell>
                                    <TableCell>Nº Plan</TableCell>
                                    <TableCell>Certificado</TableCell>
                                    <TableCell align="right">Deuda</TableCell>
                                    <TableCell align="center">Cuota</TableCell>
                                    <TableCell align="center">Estado</TableCell>
                                    <TableCell>Demandado</TableCell>
                                    <TableCell>Expediente</TableCell>
                                    <TableCell align="center">Acción</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {registros.map(r => {
                                    const cert = r.letra ? `${r.letra}-${r.numero}` : (r.numero || '—');
                                    const estadoMotor = r.Estado_Conciliacion;
                                    const tagCadena = r.Resultado_Cadena;
                                    const sel = seleccionados.has(r.Id);
                                    return (
                                        <TableRow key={r.Id} hover selected={sel}
                                            sx={{ cursor: 'pointer' }} onClick={() => onVerPlan?.(r.Id)}>
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox size="small" checked={sel} onChange={() => toggleSel(r.Id)} />
                                            </TableCell>
                                            <TableCell>{fmtFecha(r.fpago)}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.ref_nro_plan}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{cert}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>$ {fmtMonto(r.deuda_certificado)}</TableCell>
                                            <TableCell align="center">{r.Num_Cuota || '—'}</TableCell>
                                            <TableCell align="center">
                                                {estadoMotor ? (
                                                    <Tooltip title={`${estadoMotor}${tagCadena ? ' · ' + tagCadena : ''}`}>
                                                        <Chip size="small"
                                                            label={RESULTADO_LABELS[estadoMotor] || estadoMotor}
                                                            color={RESULTADO_COLORS[estadoMotor] || 'default'} />
                                                    </Tooltip>
                                                ) : tagCadena ? (
                                                    <Chip size="small" label={tagCadena} variant="outlined" />
                                                ) : <Chip size="small" label="—" variant="outlined" />}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <Tooltip title={r.Demandado_Dom || ''}><span>{r.Demandado_NN || '—'}</span></Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.Expediente_Autos || '—'}</TableCell>
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="Ver detalle del plan">
                                                    <IconButton size="small" onClick={() => onVerPlan?.(r.Id)}>
                                                        <VisibilityIcon fontSize="small" sx={{ color: COLOR }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page} rowsPerPage={pageSize}
                        onPageChange={(_, p) => { setPage(p); cargar(p, pageSize); }}
                        onRowsPerPageChange={e => { const ps = parseInt(e.target.value, 10); setPageSize(ps); setPage(0); cargar(0, ps); }}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                    />
                </>
            )}
        </Box>
    );
}

// ============================================================================
// DrawerLotes — envuelve LoteGrid + PlanesDeLote (antes TabPasaje)
// ============================================================================
function DrawerLotes({ puedeMigrar, enqueueSnackbar, onClose, onCambio }) {
    const [metricas, setMetricas] = useState(null);
    const [loteSel, setLoteSel] = useState(null);

    const cargarMetricas = useCallback(() => {
        dgrPasajeMetricasJWT().then(r => { if (r?.ok) setMetricas(r.metricas); }).catch(() => {});
    }, []);
    useEffect(() => { cargarMetricas(); }, [cargarMetricas]);

    const handleCambio = () => { cargarMetricas(); onCambio?.(); };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fafbfc' }}>
                <LayersIcon sx={{ color: COLOR }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR, lineHeight: 1.2 }}>
                        {loteSel ? `Lote del ${fmtFecha(loteSel.Fecha_Plan_Pago)}` : 'Lotes diarios — pasaje a expediente'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {loteSel ? 'Planes del lote · seleccionar y pasar a expediente' : 'Vista por lote diario para pasar planes conciliados a expediente'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                {!loteSel && metricas && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Chip label={`Conciliados: ${(metricas.Conciliados_V || 0).toLocaleString('es-AR')}`} color="success" variant="outlined" />
                        <Chip label={`🕓 Pendientes de pasar: ${(metricas.Pendientes || 0).toLocaleString('es-AR')}`} color="warning" />
                        <Chip label={`📁 Ya pasados: ${(metricas.Pasados || 0).toLocaleString('es-AR')}`} color="default" />
                    </Stack>
                )}

                {loteSel
                    ? <PlanesDeLote lote={loteSel} puedeMigrar={puedeMigrar} enqueueSnackbar={enqueueSnackbar}
                        onBack={() => setLoteSel(null)} onPasajeOk={handleCambio} />
                    : <LoteGrid onSelect={setLoteSel} enqueueSnackbar={enqueueSnackbar} />}
            </Box>
        </Box>
    );
}

// ── Grilla de lotes diarios ─────────────────────────────────────────────────
function LoteGrid({ onSelect, enqueueSnackbar }) {
    const [lotes, setLotes] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [soloPend, setSoloPend] = useState(true);   // ≈ checkbox "Pendientes de Pasaje" del viejo
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await dgrPasajeLotesJWT({
                fechaDesde, fechaHasta, soloConPendientes: soloPend,
                page: page + 1, pageSize,
            });
            if (r?.ok) { setLotes(r.items || []); setTotal(r.total || 0); }
            else { setLotes([]); setTotal(0); }
        } catch (e) {
            enqueueSnackbar('Error cargando lotes', { variant: 'error' });
        } finally { setLoading(false); }
    }, [fechaDesde, fechaHasta, soloPend, page, pageSize, enqueueSnackbar]);

    useEffect(() => { cargar(); }, [cargar]);

    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px` }}>
                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
                    <FormControlLabel
                        control={<Checkbox size="small" checked={soloPend}
                            onChange={e => { setSoloPend(e.target.checked); setPage(0); }} />}
                        label="Solo lotes con pendientes de pasar" />
                    <TextField type="date" size="small" label="Desde" InputLabelProps={{ shrink: true }}
                        value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                    <TextField type="date" size="small" label="Hasta" InputLabelProps={{ shrink: true }}
                        value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                    <Button variant="contained" size="small" startIcon={<SearchIcon />}
                        sx={{ bgcolor: COLOR }} onClick={() => { setPage(0); cargar(); }}>Buscar</Button>
                    <IconButton size="small" onClick={cargar}><RefreshIcon /></IconButton>
                </Stack>
            </Paper>

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${BR}px` }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ '& th': { bgcolor: '#fafafa', fontWeight: 700 } }}>
                                <TableCell>Fecha del lote</TableCell>
                                <TableCell>Usuario</TableCell>
                                <TableCell align="center">Planes</TableCell>
                                <TableCell align="center">Conciliados</TableCell>
                                <TableCell align="center">Pendientes de pasar</TableCell>
                                <TableCell align="center">Ya pasados</TableCell>
                                <TableCell align="right">Deuda total</TableCell>
                                <TableCell align="center"> </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lotes.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    {soloPend ? 'No hay lotes con planes pendientes de pasar.' : 'Sin lotes.'}
                                </TableCell></TableRow>
                            ) : lotes.map(l => (
                                <TableRow key={l.Id} hover sx={{ cursor: 'pointer' }} onClick={() => onSelect(l)}>
                                    <TableCell sx={{ fontWeight: 600 }}>{fmtFecha(l.Fecha_Plan_Pago)}</TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>{l.Usuario || '—'}</TableCell>
                                    <TableCell align="center">{l.Total_Planes}</TableCell>
                                    <TableCell align="center">{l.Conciliados}</TableCell>
                                    <TableCell align="center">
                                        {l.Pendientes > 0
                                            ? <Chip size="small" color="warning" label={l.Pendientes} />
                                            : <span style={{ color: '#bbb' }}>0</span>}
                                    </TableCell>
                                    <TableCell align="center">{l.Pasados}</TableCell>
                                    <TableCell align="right">{fmtMonto(l.Deuda_Total)}</TableCell>
                                    <TableCell align="center">
                                        <Button size="small" startIcon={<FolderOpenIcon />}
                                            onClick={(e) => { e.stopPropagation(); onSelect(l); }}>
                                            Abrir
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!soloPend && (
                <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]} />
            )}
        </Box>
    );
}

// ── Planes de un lote (detalle del lote seleccionado) ───────────────────────
function PlanesDeLote({ lote, puedeMigrar, enqueueSnackbar, onBack, onPasajeOk }) {
    const [estado, setEstado] = useState('pendientes');
    const [filtros, setFiltros] = useState({ numeroPlan: '', letra: '', numero: '' });
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [seleccion, setSeleccion] = useState(new Set());
    const [running, setRunning] = useState(false);
    const [exportando, setExportando] = useState(false);
    const [resultado, setResultado] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true); setSeleccion(new Set());
        try {
            const r = await dgrPasajeListarJWT({
                idLote: lote.Id,
                numeroPlan: filtros.numeroPlan ? Number(filtros.numeroPlan) : undefined,
                letra: filtros.letra || undefined,
                numero: filtros.numero ? Number(filtros.numero) : undefined,
                estado, page: page + 1, pageSize,
            });
            if (r?.ok) { setRows(r.items || []); setTotal(r.total || 0); }
            else { setRows([]); setTotal(0); }
        } finally { setLoading(false); }
    }, [lote.Id, filtros, estado, page, pageSize]);

    useEffect(() => { cargar(); }, [cargar]);

    const toggleTodos = () => {
        if (seleccion.size === rows.length) setSeleccion(new Set());
        else setSeleccion(new Set(rows.map(r => r.Id)));
    };
    const toggleUno = (id) => {
        const s = new Set(seleccion);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSeleccion(s);
    };

    const ejecutar = async () => {
        if (seleccion.size === 0) return;
        setRunning(true); setResultado(null);
        try {
            const r = await dgrPasajeEjecutarJWT(Array.from(seleccion));
            setResultado(r);
            if (r?.ok) {
                enqueueSnackbar(r.mensaje, { variant: r.fallidos > 0 ? 'warning' : 'success' });
                setSeleccion(new Set());
                cargar(); onPasajeOk?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
        finally { setRunning(false); }
    };

    // "Actualizar DGR": pasa a auditoría todos los conciliados pendientes del
    // lote, sin selección manual (Fase 2 del rediseño de conciliación/DGR).
    const actualizarTodos = async () => {
        setRunning(true); setResultado(null);
        try {
            const r = await dgrPasajeActualizarJWT({ idLote: lote.Id });
            setResultado(r);
            if (r?.ok) {
                enqueueSnackbar(r.mensaje, { variant: r.fallidos > 0 ? 'warning' : 'success' });
                setSeleccion(new Set());
                cargar(); onPasajeOk?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
        finally { setRunning(false); }
    };

    const exportar = async () => {
        setExportando(true);
        try {
            const r = await dgrPasajeListarJWT({
                idLote: lote.Id,
                numeroPlan: filtros.numeroPlan ? Number(filtros.numeroPlan) : undefined,
                letra: filtros.letra || undefined,
                numero: filtros.numero ? Number(filtros.numero) : undefined,
                estado, page: 1, pageSize: 10000,
            });
            const items = (r?.ok && Array.isArray(r.items)) ? r.items : [];
            if (items.length === 0) {
                enqueueSnackbar('No hay datos para exportar con los filtros actuales', { variant: 'info' });
                return;
            }
            const data = items.map(it => ({
                'Nº Plan': it.ref_nro_plan,
                'Letra': it.letra,
                'Nº Cert': it.numero,
                'Cuota': it.Num_Cuota || 1,
                'Demandado': it.Demandado_NN || '',
                'Domicilio': it.Demandado_Dom || '',
                'Abogado': it.Abogado_NN || '',
                'Matrícula': it.Abogado_Mat || '',
                'Deuda': Number(it.deuda_certificado || 0),
                'Costas': Number(it.costas || 0),
                'Costas IVA': Number(it.costas_iva || 0),
                'Gastos': Number(it.gastos || 0),
                'Fecha Pago': fmtFecha(it.fpago),
                'Conciliación': it.Estado ? (RESULTADO_LABELS[it.Estado] || it.Estado) : '',
                'Cruzamiento': it.Resultado_Cruzamiento === 1 ? 'OK' : it.Resultado_Cruzamiento === -1 ? 'Sin expediente' : it.Resultado_Cruzamiento === 0 ? 'Sin certificado' : 'Sin cruzar',
                'Expediente': it.Expediente_Id ? String(it.Expediente_Id) : '',
                'Autos': it.Expediente_Autos || '',
            }));
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Planes del lote');
            const fechaLote = lote.Fecha_Plan_Pago ? new Date(lote.Fecha_Plan_Pago).toISOString().slice(0, 10) : 'lote';
            XLSX.writeFile(wb, `pasaje_dgr_lote_${fechaLote}_${estado}.xlsx`);
            enqueueSnackbar(`${items.length} filas exportadas`, { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(String(e?.message || e), { variant: 'error' });
        } finally {
            setExportando(false);
        }
    };

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>Volver a lotes</Button>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLOR }}>
                    Lote del {fmtFecha(lote.Fecha_Plan_Pago)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {lote.Total_Planes} planes · {lote.Conciliados} conciliados · {lote.Pendientes} pendientes · {lote.Pasados} pasados
                </Typography>
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px` }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <TextField select size="small" label="Estado" sx={{ minWidth: 150 }}
                        value={estado} onChange={e => { setEstado(e.target.value); setPage(0); }}>
                        <MenuItem value="pendientes">Pendientes</MenuItem>
                        <MenuItem value="pasados">Ya pasados</MenuItem>
                        <MenuItem value="todos">Todos</MenuItem>
                    </TextField>
                    <TextField size="small" label="Nº Plan" sx={{ width: 120 }}
                        value={filtros.numeroPlan} onChange={e => setFiltros(f => ({ ...f, numeroPlan: e.target.value }))} />
                    <TextField size="small" label="Letra" sx={{ width: 80 }}
                        value={filtros.letra} onChange={e => setFiltros(f => ({ ...f, letra: e.target.value.toUpperCase() }))} />
                    <TextField size="small" label="Nº Cert" sx={{ width: 120 }}
                        value={filtros.numero} onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))} />
                    <Button variant="contained" size="small" startIcon={<SearchIcon />}
                        sx={{ bgcolor: COLOR }} onClick={() => { setPage(0); cargar(); }}>Buscar</Button>
                    <IconButton size="small" onClick={cargar}><RefreshIcon /></IconButton>
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Exportar a Excel los planes de este lote (con el filtro actual)">
                        <span>
                            <Button variant="outlined" size="small"
                                startIcon={exportando ? <CircularProgress size={14} /> : <FileDownloadIcon />}
                                onClick={exportar} disabled={exportando}>
                                Exportar Excel
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Paper>

            {puedeMigrar && estado === 'pendientes' && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {seleccion.size} seleccionado{seleccion.size !== 1 ? 's' : ''}
                    </Typography>
                    <Button variant="contained" size="small" startIcon={<PlayIcon />}
                        disabled={seleccion.size === 0 || running} onClick={ejecutar} sx={{ bgcolor: COLOR }}>
                        {running ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Pasar a Auditoría'}
                    </Button>
                    <Tooltip title="Pasa a auditoría TODOS los planes conciliados pendientes de este lote, sin seleccionar uno por uno.">
                        <span>
                            <Button variant="outlined" size="small" startIcon={running ? <CircularProgress size={14} /> : <PlayIcon />}
                                disabled={running} onClick={actualizarTodos} sx={{ color: COLOR, borderColor: COLOR }}>
                                Actualizar DGR (todos)
                            </Button>
                        </span>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                        Crea expediente, certificado, partes (repartición + demandado), contraparte y movimiento. Las columnas Demandado/Abogado se resuelven en vivo contra el padrón actual.
                    </Typography>
                </Stack>
            )}

            {resultado?.ok && (
                <Alert severity={resultado.fallidos > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    {resultado.mensaje}
                    {resultado.detalles?.some(d => !d.ok) && (
                        <Box sx={{ mt: 1, maxHeight: 140, overflow: 'auto' }}>
                            {resultado.detalles.filter(d => !d.ok).slice(0, 20).map(d => (
                                <div key={d.idDetalle} style={{ fontSize: 12 }}>• Id={d.idDetalle}: {d.motivo}</div>
                            ))}
                        </Box>
                    )}
                </Alert>
            )}

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${BR}px` }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {puedeMigrar && estado === 'pendientes' && (
                                    <TableCell sx={{ bgcolor: '#fafafa', width: 40 }} padding="checkbox">
                                        <Checkbox size="small" indeterminate={seleccion.size > 0 && seleccion.size < rows.length}
                                            checked={rows.length > 0 && seleccion.size === rows.length}
                                            onChange={toggleTodos} />
                                    </TableCell>
                                )}
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Nº Plan</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Cert.</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Cuota</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Demandado</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Abogado</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Deuda</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha Pago</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Expediente</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin resultados</TableCell></TableRow>
                            ) : rows.map(r => (
                                <TableRow key={r.Id} hover selected={seleccion.has(r.Id)}>
                                    {puedeMigrar && estado === 'pendientes' && (
                                        <TableCell padding="checkbox">
                                            <Checkbox size="small" checked={seleccion.has(r.Id)}
                                                onChange={() => toggleUno(r.Id)} />
                                        </TableCell>
                                    )}
                                    <TableCell>{r.ref_nro_plan}</TableCell>
                                    <TableCell>{r.letra}-{r.numero}</TableCell>
                                    <TableCell align="center">{r.Num_Cuota || 1}</TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>{(r.Demandado_NN || '').slice(0, 50)}</TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>{(r.Abogado_NN || '').slice(0, 30)}<br /><span style={{ color: '#888' }}>{r.Abogado_Mat}</span></TableCell>
                                    <TableCell align="right">{fmtMonto(r.deuda_certificado)}</TableCell>
                                    <TableCell>{fmtFecha(r.fpago)}</TableCell>
                                    <TableCell align="center">
                                        {r.Expediente_Id
                                            ? <Chip size="small" color="success" label={`#${r.Expediente_Id}`} />
                                            : r.Resultado_Cruzamiento === 1 && r.Auditado === 0
                                                ? <Tooltip title="Cert + expediente + abogado resueltos. Listo para pasar."><Chip size="small" color="success" variant="outlined" label="Cruzado OK" /></Tooltip>
                                                : r.Resultado_Cruzamiento === 1 && r.Auditado === -1
                                                    ? <Tooltip title="Expediente encontrado pero sin abogado apoderado activo en Partes."><Chip size="small" color="warning" label="Sin abogado" /></Tooltip>
                                                    : r.Resultado_Cruzamiento === -1
                                                        ? <Tooltip title="El certificado existe pero no tiene expediente vinculado."><Chip size="small" color="warning" label="Cert sin exp" /></Tooltip>
                                                        : r.Resultado_Cruzamiento === 0
                                                            ? <Tooltip title="No existe certificado con esa letra/número en Rentas."><Chip size="small" color="warning" label="Sin cert" /></Tooltip>
                                                            : <Chip size="small" variant="outlined" label="Pendiente" />}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100, 200]} />
        </Box>
    );
}

// ============================================================
// TAB: Pendientes de expediente (Fase 3 no-fantasma)
// ============================================================
// Planes conciliados (V) que NO tienen expediente. En lugar de crear
// expedientes fantasma, esperan a que Carga Masiva cree el expediente real
// (se enganchan solos por Serie+Numero). Desde acá el operador puede ver los
// que esperan y, si hace falta, forzar la creación manual ("Crear expediente
// igual").
function DrawerPendientesExp({ puedeMigrar, enqueueSnackbar, onClose, onCambio }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [creando, setCreando] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await dgrPasajeListarJWT({
                estado: 'pendientes',
                soloConciliados: true,
                page: page + 1, pageSize,
            });
            if (r?.ok) { setRows(r.items || []); setTotal(r.total || 0); }
            else { setRows([]); setTotal(0); }
        } finally { setLoading(false); }
    }, [page, pageSize]);

    useEffect(() => { cargar(); }, [cargar]);

    const crearIgual = async (id) => {
        setCreando(id);
        try {
            const r = await dgrPasajeForzarCrearJWT(id);
            if (r?.ok) {
                enqueueSnackbar(r.motivo || 'Expediente creado', { variant: 'success' });
                cargar(); onCambio?.();
            } else enqueueSnackbar(r?.motivo || r?.mensaje || 'Error', { variant: 'error' });
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
        finally { setCreando(null); }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fafbfc' }}>
                <PendingActionsIcon sx={{ color: COLOR }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR, lineHeight: 1.2 }}>
                        Planes conciliados sin expediente
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {total.toLocaleString('es-AR')} planes esperando ser vinculados
                    </Typography>
                </Box>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Se vinculan automáticamente cuando Carga Masiva crea el expediente real (por Serie+Número).
                    Usá <strong>"Crear expediente igual"</strong> solo si necesitás el expediente ya, sin esperar.
                </Alert>

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${BR}px` }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Nº Plan</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Cert.</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Cuota</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Demandado</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Deuda</TableCell>
                                {puedeMigrar && <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Acción</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={puedeMigrar ? 7 : 6} align="center" sx={{ color: '#999', py: 3 }}>
                                    No hay planes conciliados pendientes de expediente.
                                </TableCell></TableRow>
                            )}
                            {rows.map(r => (
                                <TableRow key={r.Id} hover>
                                    <TableCell>{r.ref_nro_plan}</TableCell>
                                    <TableCell>{r.letra}-{r.numero}</TableCell>
                                    <TableCell align="center">{r.Num_Cuota || 1}</TableCell>
                                    <TableCell>{fmtFecha(r.fpago)}</TableCell>
                                    <TableCell>{r.Demandado_NN || '-'}</TableCell>
                                    <TableCell align="right">{r.deuda_certificado != null ? `$${fmtMonto(r.deuda_certificado)}` : '-'}</TableCell>
                                    {puedeMigrar && (
                                        <TableCell align="center">
                                            <Button size="small" variant="outlined"
                                                startIcon={creando === r.Id ? <CircularProgress size={14} /> : <FolderOpenIcon />}
                                                disabled={creando === r.Id}
                                                onClick={() => crearIgual(r.Id)}
                                                sx={{ color: COLOR, borderColor: COLOR, textTransform: 'none' }}>
                                                Crear expediente igual
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

                <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100, 200]} />
            </Box>
        </Box>
    );
}

// ============================================================================
// DrawerCambios — diferencias entre WS-DGR y datos locales (antes Tab Cambios)
// ============================================================================
const CAMPOS_COMPARABLES = [
    { key: 'deuda_certificado', label: 'Deuda certificado', tipo: 'money' },
    { key: 'costas', label: 'Costas', tipo: 'money' },
    { key: 'costas_iva', label: 'Costas IVA', tipo: 'money' },
    { key: 'gastos', label: 'Gastos', tipo: 'money' },
    { key: 'pagado', label: 'Pagado', tipo: 'text' },
    { key: 'num_cuota', label: 'Nro cuota', tipo: 'int' },
    { key: 'total_cuotas_honorarios', label: 'Total cuotas hon.', tipo: 'int' },
];

function fmtValor(v, tipo) {
    if (v == null) return '—';
    if (tipo === 'money') return `$ ${fmtMonto(v)}`;
    return String(v);
}

function DrawerCambios({ puedeMigrar, enqueueSnackbar, onClose, onCambio }) {
    const [estado, setEstado] = useState('PENDIENTE');
    const [diferencias, setDiferencias] = useState([]);
    const [total, setTotal] = useState(0);
    const [contadores, setContadores] = useState({ pendientes: 0, aceptadas: 0, rechazadas: 0, revertidas: 0 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [seleccionados, setSeleccionados] = useState(new Set());
    const [obsDialog, setObsDialog] = useState(null);
    const [obsTexto, setObsTexto] = useState('');
    const [exportando, setExportando] = useState(false);

    const cargarContadores = useCallback(() => {
        dgrDiferenciasContadoresJWT().then(r => { if (r?.ok) setContadores(r.contadores); }).catch(() => {});
    }, []);

    const notificarCambio = () => { onCambio?.(); };

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await dgrDiferenciasListarJWT({ estado, page: page + 1, pageSize });
            if (r?.ok) {
                setDiferencias(r.items || []);
                setTotal(r.total || 0);
                setSeleccionados(new Set());
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al cargar diferencias', { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Error al cargar diferencias', { variant: 'error' });
        } finally { setLoading(false); }
    }, [estado, page, pageSize, enqueueSnackbar]);

    useEffect(() => { cargar(); cargarContadores(); }, [cargar, cargarContadores]);

    const handleAceptar = async (id, observacion) => {
        const r = await dgrDiferenciaAceptarJWT(id, observacion);
        if (r?.ok) {
            enqueueSnackbar('Cambio aplicado', { variant: 'success' });
            cargar(); cargarContadores(); notificarCambio();
        } else { enqueueSnackbar(r?.mensaje || 'Error al aceptar', { variant: 'error' }); }
    };
    const handleRechazar = async (id, observacion) => {
        const r = await dgrDiferenciaRechazarJWT(id, observacion);
        if (r?.ok) {
            enqueueSnackbar('Diferencia rechazada', { variant: 'success' });
            cargar(); cargarContadores(); notificarCambio();
        } else { enqueueSnackbar(r?.mensaje || 'Error al rechazar', { variant: 'error' }); }
    };
    const handleRevertir = async (id, observacion) => {
        const r = await dgrDiferenciaRevertirJWT(id, observacion);
        if (r?.ok) {
            enqueueSnackbar(r.mensaje || 'Cambio revertido', { variant: 'success' });
            cargar(); cargarContadores(); notificarCambio();
        } else { enqueueSnackbar(r?.mensaje || 'Error al revertir', { variant: 'error' }); }
    };

    // ─── EXPORT ────────────────────────────────────────────────────────────
    const tituloReporte = () => {
        const partes = ['Cambios detectados WS-DGR'];
        partes.push(`Filtro: ${estado}`);
        partes.push(`Generado: ${new Date().toLocaleString('es-AR')}`);
        return partes.join(' — ');
    };

    const cargarTodosParaExport = async () => {
        const r = await dgrDiferenciasExportarJWT({ estado });
        return r?.ok ? (r.items || []) : [];
    };

    const filaParaTabla = (d) => {
        const local = d.Datos_Locales || {};
        const ws = d.Datos_WS || {};
        const cambiadosLabels = CAMPOS_COMPARABLES
            .filter(c => JSON.stringify(local[c.key]) !== JSON.stringify(ws[c.key]))
            .map(c => `${c.label}: ${fmtValor(local[c.key], c.tipo)} → ${fmtValor(ws[c.key], c.tipo)}`)
            .join(' · ');
        return {
            id: d.Id,
            fechaLote: fmtFecha(d.Fecha_Plan_Pago),
            plan: d.ref_nro_plan,
            certificado: `${d.letra || ''}-${d.numero || ''}`,
            expediente: d.Expediente_Id || '',
            estado_detalle: d.Estado_Detalle || '',
            auditado: d.Auditado === 1 ? 'Sí' : 'No',
            detectado: fmtFecha(d.Fecha_Deteccion),
            detectado_por: d.Detectado_Por || '',
            cambios: cambiadosLabels,
            estado: d.Estado,
            resuelto: d.Fecha_Resolucion ? `${fmtFecha(d.Fecha_Resolucion)} (${d.Resuelto_Por || ''})` : '',
            revertido: d.Fecha_Reversion ? `${fmtFecha(d.Fecha_Reversion)} (${d.Revertido_Por || ''})` : '',
            obs: d.Observacion || '',
            obs_rev: d.Observacion_Reversion || '',
        };
    };

    const handleExportExcel = async () => {
        setExportando(true);
        try {
            const items = await cargarTodosParaExport();
            const filas = items.map(filaParaTabla);
            const wb = XLSX.utils.book_new();
            const head = [
                ['Id', 'Fecha Lote', 'Plan', 'Certificado', 'Expediente', 'Estado detalle', 'Auditado',
                 'Detectado', 'Por', 'Cambios', 'Estado', 'Resuelto', 'Observación', 'Revertido', 'Obs. reversión'],
            ];
            const body = filas.map(f => [
                f.id, f.fechaLote, f.plan, f.certificado, f.expediente, f.estado_detalle, f.auditado,
                f.detectado, f.detectado_por, f.cambios, f.estado, f.resuelto, f.obs, f.revertido, f.obs_rev,
            ]);
            const ws = XLSX.utils.aoa_to_sheet([...head, ...body]);
            ws['!cols'] = [
                { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 10 },
                { wch: 12 }, { wch: 14 }, { wch: 60 }, { wch: 12 }, { wch: 22 }, { wch: 30 }, { wch: 22 }, { wch: 30 },
            ];
            XLSX.utils.book_append_sheet(wb, ws, 'Diferencias');
            const fecha = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Diferencias_DGR_${estado}_${fecha}.xlsx`);
        } catch (e) {
            enqueueSnackbar('Error al exportar', { variant: 'error' });
        } finally { setExportando(false); }
    };

    const handleExportPDF = async () => {
        setExportando(true);
        try {
            const items = await cargarTodosParaExport();
            const filas = items.map(filaParaTabla);
            const doc = new jsPDF({ orientation: 'landscape' });
            const margin = 14;
            doc.setFontSize(14);
            doc.setTextColor(21, 101, 192);
            doc.text(tituloReporte(), margin, 15);
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(`${filas.length} registro(s)`, margin, 21);

            autoTable(doc, {
                startY: 26,
                head: [['Fecha', 'Plan', 'Cert.', 'Exp.', 'Detect.', 'Cambios', 'Estado']],
                body: filas.map(f => [
                    f.fechaLote, f.plan, f.certificado, f.expediente || '-',
                    f.detectado, f.cambios, f.estado,
                ]),
                theme: 'striped',
                headStyles: { fillColor: [21, 101, 192] },
                styles: { fontSize: 7, cellPadding: 1.5 },
                columnStyles: {
                    5: { cellWidth: 95 },
                },
                margin: { left: margin, right: margin },
            });

            const fecha = new Date().toISOString().slice(0, 10);
            const filename = `Diferencias_DGR_${estado}_${fecha}.pdf`;
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) doc.save(filename);
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) {
            enqueueSnackbar('Error al exportar PDF', { variant: 'error' });
        } finally { setExportando(false); }
    };

    const handleBatch = async (accion) => {
        const ids = Array.from(seleccionados);
        if (ids.length === 0) return;
        const fn = accion === 'aceptar' ? dgrDiferenciasAceptarBatchJWT : dgrDiferenciasRechazarBatchJWT;
        const r = await fn(ids);
        if (r?.ok) {
            enqueueSnackbar(`${accion === 'aceptar' ? r.aceptados : r.rechazados} procesados${r.errores > 0 ? ` (${r.errores} fallaron)` : ''}`, { variant: 'success' });
            cargar(); cargarContadores(); notificarCambio();
        }
    };

    const [purgando, setPurgando] = useState(false);
    const handlePurgarRuido = async () => {
        setPurgando(true);
        try {
            const r = await dgrDiferenciasPurgarRuidoJWT();
            if (r?.ok) {
                enqueueSnackbar(r.mensaje || `Purgadas ${r.purgadas} diferencias por ruido de float32`, { variant: 'success' });
                cargar(); cargarContadores(); notificarCambio();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al purgar', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Error al purgar diferencias', { variant: 'error' });
        } finally { setPurgando(false); }
    };

    const toggleSel = (id) => {
        const s = new Set(seleccionados);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSeleccionados(s);
    };
    const toggleAll = () => {
        if (seleccionados.size === diferencias.length) setSeleccionados(new Set());
        else setSeleccionados(new Set(diferencias.map(d => d.Id)));
    };

    const abrirDialog = (id, accion) => { setObsDialog({ id, accion }); setObsTexto(''); };
    const confirmarDialog = async () => {
        if (!obsDialog) return;
        const fn = obsDialog.accion === 'aceptar' ? handleAceptar
            : obsDialog.accion === 'rechazar' ? handleRechazar
            : handleRevertir;
        await fn(obsDialog.id, obsTexto);
        setObsDialog(null); setObsTexto('');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fafbfc' }}>
                <CompareArrowsIcon sx={{ color: COLOR }} />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR, lineHeight: 1.2 }}>
                        Cambios detectados
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Diferencias entre lo que viene del WS-DGR y los datos locales — esperan tu decisión
                    </Typography>
                </Box>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, mr: 0.5 }}>FILTRAR POR ESTADO:</Typography>
                <Chip label={`Pendientes: ${contadores.pendientes}`} color={estado === 'PENDIENTE' ? 'warning' : 'default'}
                    variant={estado === 'PENDIENTE' ? 'filled' : 'outlined'}
                    onClick={() => { setEstado('PENDIENTE'); setPage(0); }} />
                <Chip label={`Aceptadas: ${contadores.aceptadas}`} color={estado === 'ACEPTADO' ? 'success' : 'default'}
                    variant={estado === 'ACEPTADO' ? 'filled' : 'outlined'}
                    onClick={() => { setEstado('ACEPTADO'); setPage(0); }} />
                <Chip label={`Rechazadas: ${contadores.rechazadas}`} color={estado === 'RECHAZADO' ? 'error' : 'default'}
                    variant={estado === 'RECHAZADO' ? 'filled' : 'outlined'}
                    onClick={() => { setEstado('RECHAZADO'); setPage(0); }} />
                <Chip label={`Revertidas: ${contadores.revertidas}`} color={estado === 'REVERTIDA' ? 'primary' : 'default'}
                    variant={estado === 'REVERTIDA' ? 'filled' : 'outlined'}
                    onClick={() => { setEstado('REVERTIDA'); setPage(0); }} />
                <Chip label="Todas" variant={estado === 'TODOS' ? 'filled' : 'outlined'}
                    onClick={() => { setEstado('TODOS'); setPage(0); }} />
                <Box sx={{ flexGrow: 1 }} />
                {estado === 'PENDIENTE' && contadores.pendientes > 0 && puedeMigrar && (
                    <Tooltip title="Marca como rechazadas las diferencias cuya delta sea solo ruido del tipo Real de SQL Server (< 1 centavo o tolerancia proporcional al monto). Útil para limpiar el listado de cambios fantasma.">
                        <span>
                            <Button size="small" variant="outlined" color="warning"
                                onClick={handlePurgarRuido} disabled={purgando}
                                startIcon={purgando ? <CircularProgress size={14} /> : null}>
                                Purgar ruido (float32)
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {estado === 'PENDIENTE' && seleccionados.size > 0 && puedeMigrar && (
                    <>
                        <Button size="small" color="success" variant="contained" startIcon={<CheckCircleIcon />}
                            onClick={() => handleBatch('aceptar')}>
                            Aceptar {seleccionados.size}
                        </Button>
                        <Button size="small" color="error" variant="outlined" startIcon={<CancelIcon />}
                            onClick={() => handleBatch('rechazar')}>
                            Rechazar {seleccionados.size}
                        </Button>
                    </>
                )}
                <Tooltip title="Exportar a Excel">
                    <span>
                        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                            onClick={handleExportExcel} disabled={exportando || total === 0}>
                            Excel
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Exportar a PDF (preview en nueva pestaña)">
                    <span>
                        <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPDF} disabled={exportando || total === 0}>
                            PDF
                        </Button>
                    </span>
                </Tooltip>
                <IconButton size="small" onClick={cargar}><RefreshIcon /></IconButton>
            </Stack>

            {loading && <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}

            {!loading && diferencias.length === 0 && (
                <Alert severity={estado === 'PENDIENTE' ? 'success' : 'info'} sx={{ mt: 2 }}>
                    {estado === 'PENDIENTE'
                        ? 'No hay diferencias pendientes de revisión. Todo lo importado del WS-DGR coincide con la base local.'
                        : 'No hay registros en este estado.'}
                </Alert>
            )}

            {!loading && diferencias.length > 0 && (
                <Stack spacing={2}>
                    {estado === 'PENDIENTE' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox size="small"
                                checked={seleccionados.size === diferencias.length && diferencias.length > 0}
                                indeterminate={seleccionados.size > 0 && seleccionados.size < diferencias.length}
                                onChange={toggleAll} />
                            <Typography variant="caption" sx={{ color: '#666' }}>Seleccionar todos en la página</Typography>
                        </Box>
                    )}
                    {diferencias.map(dif => {
                        const local = dif.Datos_Locales || {};
                        const ws = dif.Datos_WS || {};
                        const cambiados = CAMPOS_COMPARABLES.filter(c => JSON.stringify(local[c.key]) !== JSON.stringify(ws[c.key]));
                        return (
                            <Paper key={dif.Id} variant="outlined" sx={{ p: 2, borderRadius: `${BR}px`, borderLeft: `4px solid ${
                                dif.Estado === 'PENDIENTE' ? '#f57c00'
                                : dif.Estado === 'ACEPTADO' ? '#2e7d32'
                                : dif.Estado === 'REVERTIDA' ? '#1565c0'
                                : '#c62828'}` }}>
                                <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                                    {estado === 'PENDIENTE' && puedeMigrar && (
                                        <Checkbox size="small" checked={seleccionados.has(dif.Id)} onChange={() => toggleSel(dif.Id)} />
                                    )}
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        Plan {dif.ref_nro_plan} · Cert. {dif.letra}-{dif.numero}
                                    </Typography>
                                    <Chip size="small" label={`Lote del ${fmtFecha(dif.Fecha_Plan_Pago)}`} variant="outlined" />
                                    {dif.Estado_Detalle && <Chip size="small" label={`Estado: ${dif.Estado_Detalle}`} variant="outlined" />}
                                    {dif.Expediente_Id && <Chip size="small" label={`Exp ${dif.Expediente_Id}`} color="info" variant="outlined" />}
                                    {dif.Auditado === 1 && <Chip size="small" label="Auditado" color="success" variant="outlined" />}
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Typography variant="caption" sx={{ color: '#666' }}>
                                        Detectado: {fmtFecha(dif.Fecha_Deteccion)} por {dif.Detectado_Por || '—'}
                                    </Typography>
                                </Stack>

                                <Table size="small" sx={{ '& th': { bgcolor: '#fafafa', fontWeight: 700 } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Campo</TableCell>
                                            <TableCell align="right">Valor local</TableCell>
                                            <TableCell align="right">Valor del WS-DGR</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cambiados.map(c => (
                                            <TableRow key={c.key} sx={{ bgcolor: '#fffbe6' }}>
                                                <TableCell>{c.label}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                    {fmtValor(local[c.key], c.tipo)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1565c0' }}>
                                                    {fmtValor(ws[c.key], c.tipo)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                {dif.Estado === 'PENDIENTE' && puedeMigrar && (
                                    <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                                        <Button size="small" color="error" startIcon={<CancelIcon />}
                                            onClick={() => abrirDialog(dif.Id, 'rechazar')}>
                                            Rechazar
                                        </Button>
                                        <Button size="small" color="success" variant="contained" startIcon={<CheckCircleIcon />}
                                            onClick={() => abrirDialog(dif.Id, 'aceptar')}>
                                            Aceptar cambio
                                        </Button>
                                    </Stack>
                                )}

                                {dif.Estado !== 'PENDIENTE' && (
                                    <Box sx={{ mt: 1.5, p: 1, bgcolor: '#fafafa', borderRadius: 1, fontSize: '0.85rem' }}>
                                        <strong>Resuelto:</strong> {fmtFecha(dif.Fecha_Resolucion)} por {dif.Resuelto_Por || '—'} · Estado: <strong>{dif.Estado}</strong>
                                        {dif.Observacion && <Box sx={{ mt: 0.5, fontStyle: 'italic', color: '#666' }}>"{dif.Observacion}"</Box>}
                                        {dif.Fecha_Reversion && (
                                            <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #ccc' }}>
                                                <strong>Revertido:</strong> {fmtFecha(dif.Fecha_Reversion)} por {dif.Revertido_Por || '—'}
                                                {dif.Observacion_Reversion && <Box sx={{ fontStyle: 'italic', color: '#666' }}>"{dif.Observacion_Reversion}"</Box>}
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {dif.Estado === 'ACEPTADO' && puedeMigrar && dif.Snapshot_Antes && (
                                    <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                                        <Button size="small" color="primary" variant="outlined" startIcon={<UndoIcon />}
                                            onClick={() => abrirDialog(dif.Id, 'revertir')}>
                                            Revertir cambio
                                        </Button>
                                    </Stack>
                                )}
                            </Paper>
                        );
                    })}
                </Stack>
            )}

            <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]} sx={{ mt: 2 }} />

            <Dialog open={!!obsDialog} onClose={() => setObsDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {obsDialog?.accion === 'aceptar' ? 'Aceptar cambio'
                        : obsDialog?.accion === 'rechazar' ? 'Rechazar diferencia'
                        : 'Revertir cambio aceptado'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                        {obsDialog?.accion === 'aceptar' &&
                            'Vas a aplicar los valores del WS-DGR al detalle local. Se guardará un snapshot del valor anterior por si necesitás revertir.'}
                        {obsDialog?.accion === 'rechazar' &&
                            'Vas a rechazar el cambio. El detalle local queda como está y esta diferencia se archiva como RECHAZADA.'}
                        {obsDialog?.accion === 'revertir' &&
                            'Vas a revertir el cambio aceptado: el detalle local volverá al valor anterior usando el snapshot guardado. La diferencia quedará marcada como REVERTIDA.'}
                    </Typography>
                    <TextField fullWidth label="Observación (opcional)" multiline rows={3}
                        value={obsTexto} onChange={e => setObsTexto(e.target.value)}
                        placeholder="Motivo de la decisión, referencia, etc." />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setObsDialog(null)}>Cancelar</Button>
                    <Button onClick={confirmarDialog} variant="contained"
                        color={obsDialog?.accion === 'aceptar' ? 'success'
                            : obsDialog?.accion === 'rechazar' ? 'error' : 'primary'}>
                        Confirmar {obsDialog?.accion === 'aceptar' ? 'aceptación'
                            : obsDialog?.accion === 'rechazar' ? 'rechazo' : 'reversión'}
                    </Button>
                </DialogActions>
            </Dialog>
            </Box>
        </Box>
    );
}
